import {
  SourceInterface,
  search,
  useSource,
  useUrlParam,
} from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import { $parse } from "@opendash/plugin-parse";
import { Config, Source } from "@opendash/plugin-parse/dist/types-generated";
import { Button, Modal, Space, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";
import { Item } from "../../../../js/parsegenerated";
import {
  CONFIG_KEY,
  DEFAULT_CONFIG,
  ItemTableConfig,
} from "../../types/ItemsTableConfig";
import { DynamicColumn } from "./DynamicColumn";

export default observer(
  (props: {
    onEdit?: Function;
    filterSource?: boolean;
    showActions?: boolean;
    reload?: number;
  }) => {
    const t = useTranslation();
    const [items, setItems] = useState<Item[]>([]);
    const [reload, setReload] = useState(1);
    const [config, setConfig] = useState<ItemTableConfig[]>([]);
    const [searchString] = useUrlParam<string>("query", "", "string");
    const [selectedSource] = useSource();
    const { confirm } = Modal;

    const columns: ColumnsType<Item> = [
      {
        title: t("maintenance:item.table.label.name"),
        dataIndex: "name",
        key: "name",
        render: (_, record) => record.get("name"),
        onFilter: (value: string, record) => record.get("name").includes(value),
        filterSearch: true,
      },
    ];

    const tableItems = config
      .filter((item) => item.show)
      .sort((a, b) => a.order - b.order);

    for (let i in tableItems) {
      const configItem = tableItems[i];
      columns.push({
        title: t("maintenance:item.table.label." + configItem.attribute),
        dataIndex: configItem.attribute,
        key: configItem.attribute,
        render: (_, record) => DynamicColumn(record, configItem.attribute),
      });
    }

    if (props.showActions)
      columns.push({
        title: t("maintenance:item.table.label.actions"),
        key: "action",
        render: (_, record) => (
          <Space size="small">
            <>
              {$parse.ui.getObjectPermission(record, "delete") ? (
                <Tooltip
                  title={t(
                    "maintenance:items.manage.table.columns.actions.delete.tooltip"
                  )}
                >
                  <Button
                    shape="circle"
                    danger
                    onClick={() => {
                      confirm({
                        title: t(
                          "maintenance:items.manage.table.columns.actions.delete.confirm.title"
                        ),
                        icon: <Icon icon="fa:exclamation"></Icon>,
                        content: t(
                          "maintenance:items.manage.table.columns.actions.delete.confirm.content"
                        ),
                        okText: t("maintenance:global.button.ok_label"),
                        okType: "danger",
                        cancelText: t("maintenance:global.button.cancel_label"),
                        onOk() {
                          deleteconfirm(record);
                        },
                        onCancel() {},
                      });
                    }}
                    icon={<Icon icon="fa:trash"></Icon>}
                  />
                </Tooltip>
              ) : null}
              {$parse.ui.getObjectPermission(record, "update") ? (
                <Tooltip
                  title={t(
                    "maintenance:items.manage.table.columns.actions.edit.tooltip"
                  )}
                >
                  <Button
                    shape="circle"
                    onClick={() => {
                      if (props.onEdit) props.onEdit(record);
                    }}
                    icon={<Icon icon="fa:pen"></Icon>}
                  />
                </Tooltip>
              ) : null}
            </>
          </Space>
        ),
      });

    const rows = useMemo(
      () =>
        search(
          searchString === null ? "" : searchString,
          items,
          (row: Item) => {
            return {
              text: [row.get("name"), row.get("description")],
              keys: {
                name: row.get("name"),
                description: row.get("description"),
              },
            };
          }
        ),
      [items.length, searchString]
    );

    const deleteconfirm = async (record: Item) => {
      await record.destroy();
      setReload((reload) => reload + 1);
    };

    // Fetching the items and config
    useEffect(() => {
      async function fetchData() {
        // getting the config
        const configQuery = new Parse.Query(Config).equalTo("key", CONFIG_KEY);
        const config = await configQuery.first();
        if (config) setConfig(await JSON.parse(config.value));
        else setConfig(DEFAULT_CONFIG);

        // gettingt the items
        const items = await new Parse.Query(Item).find();
        if (!props.filterSource) setItems(items);
        // if source filter is on
        else {
          // get all child sources (deep, recursive) of the selected source
          const sources = await findValidSources(selectedSource);
          const filteredItems: Item[] = [];
          for (const key in items) {
            const item = items[key];
            const itemSources = await item.sources.query().find();
            // add the item if it has no assigned sources
            if (itemSources.length === 0) {
              filteredItems.push(item);
              continue;
            }

            // add the item if the source is valid (selected source is one of the assigned sources or child)
            const isValid = itemSources.some((iS) =>
              sources.find((source) => source.id === iS.id)
            );
            if (isValid) filteredItems.push(item);
          }

          setItems(filteredItems);
        }
      }

      fetchData();
    }, [reload, props.reload]);

    return (
      <Table
        size="middle"
        style={{ marginTop: 24 }}
        columns={columns}
        dataSource={rows}
        rowKey={(record) => record.id}
      />
    );
  }
);

async function findValidSources(sourceInterface: SourceInterface) {
  const allSources = await new Parse.Query(Source).include("parent").find();
  const baseSource = allSources.find(
    (source) => source.id === sourceInterface.id
  );

  const allValidChildSources: Source[] = [];
  getAllChildSources(baseSource, allSources, allValidChildSources);
  return [baseSource, ...allValidChildSources];
}

/**
 * Fills the referenced array with all child sources of the given source recursively
 * @param parentSource
 * @param allSources all sources that should be checked
 * @param referencedArray the array thats going to be filled
 */
function getAllChildSources(
  parentSource: Source,
  allSources: Source[],
  referencedArray: Source[]
) {
  for (const key in allSources) {
    const source = allSources[key];
    if (source?.parent?.id === parentSource?.id) {
      referencedArray.push(source);
      getAllChildSources(source, allSources, referencedArray);
    }
  }
}
